import { useMemo } from 'react';
import { mutate } from 'swr';
import container from 'src/container';
import { getFullName } from 'src/domain/user';
import { useConsumer } from 'src/view/hooks/consumer';
import { NextOfKin } from 'src/domain/next-of-kin';
import { useAvatar } from 'src/view/hooks/useAvatar';
import { ProfileBanner } from './ProfileBanner';
// import { ImageList } from '../Image/list';
import { NextOfKinList } from '../NextOfKin/list';
// import { SessionList } from '../Session/list';
import { ConsumerProfileEdit } from './ConsumerProfileEdit';
import {
    // useHistory,
    useLocation,
    useParams,
} from 'react-router-dom';
import { useNotification } from 'src/components/Notification/useNotfication';
import Tabs from 'src/components/Navigation/Tabs/Tabs';
// import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { useTranslation } from 'react-i18next';
import { useCurrentOrganization } from 'src/view/hooks';
import { ConsumerMessagesTab } from './ConsumerMessagesTab';

const {
    cradle: { munduClient },
} = container;

export const UserProfile = () => {
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const {
        consumer,
        // refresh
    } = useConsumer(id);
    const { src, isDefault } = useAvatar(consumer?.profile.avatar);
    // const history = useHistory();
    // const user = useSignedInUser();
    const notify = useNotification();
    const { t } = useTranslation();
    const { organizationId, organization } = useCurrentOrganization();

    const groupLabels = useMemo(() => {
        const firstGroupId = consumer?.groupIds?.[0];
        if (!firstGroupId) return undefined;

        const groups = organization?.groups ?? [];
        const primary = groups.find(group => group.id === firstGroupId);
        if (!primary) return { primaryLabel: firstGroupId };

        const parent = primary.parentId
            ? groups.find(group => group.id === primary.parentId)
            : undefined;

        return {
            primaryLabel: primary.name || firstGroupId,
            parentLabel: parent?.name || parent?.id,
        };
    }, [consumer?.groupIds, organization?.groups]);

    // const onDeleteSession = useCallback(
    //     async (id: string) => {
    //         munduClient
    //             .deleteSession(id)
    //             .then(() => {
    //                 notify(t('sessions.notifications.deleted'));
    //                 mutate([consumer?.id, 'sessions']);
    //             })
    //             .catch((error: Error) => {
    //                 notify(error.message, true);
    //             });
    //     },
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    //     [refresh],
    // );

    const initialActiveTab = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');

        if (tab === 'messages') return t('users.profile.tabs.messages');
        if (tab === 'nextOfKin') return t('users.profile.tabs.nextOfKin');
        if (tab === 'profile') return t('users.profile.tabs.profile');

        return undefined;
    }, [location.search, t]);

    if (!consumer) return null;

    const onDeleteNextOfKin = async (id: NextOfKin['id']): Promise<void> => {
        munduClient
            .deleteNextOfKin(id)
            .then(() => {
                notify(t('nextOfKin.notifications.deleted'));
                mutate([consumer.id, 'nextOfKin/consumerId']);
            })
            .catch((error: Error) => notify(error.message, true));
    };

    return (
        <div className="flex flex-col h-full w-full relative text-woodyBrown">
            <ProfileBanner
                name={`${getFullName(consumer)}`}
                role={
                    groupLabels?.parentLabel && groupLabels?.primaryLabel
                        ? `${groupLabels.parentLabel} | ${groupLabels.primaryLabel}`
                        : groupLabels?.primaryLabel
                }
                avatar={src}
                rounded={isDefault}
            />

            <Tabs
                key={`${id}:${initialActiveTab ?? 'default'}`}
                initialActiveTab={initialActiveTab}
            >
                <div
                    id={t('users.profile.tabs.messages')}
                    className="space-y-4"
                >
                    {organizationId && (
                        <ConsumerMessagesTab
                            organizationId={organizationId}
                            consumerId={consumer.id}
                        />
                    )}
                </div>
                {/* <div id={t('users.profile.tabs.images')} className="space-y-4">
                    <ImageList
                        consumerId={consumer.id}
                        onAddImages={() => history.push('/app/image-upload')}
                    />
                </div> */}
                <div
                    id={t('users.profile.tabs.nextOfKin')}
                    className="space-y-4"
                >
                    <NextOfKinList
                        consumerId={consumer.id}
                        onDelete={onDeleteNextOfKin}
                    />
                </div>
                <div id={t('users.profile.tabs.profile')} className="space-y-4">
                    <ConsumerProfileEdit consumer={consumer} />
                </div>
                {/* <div
                    id={t('users.profile.tabs.sessions')}
                    className="space-y-4"
                >
                    <SessionList
                        recipientId={consumer.id}
                        onDelete={onDeleteSession}
                    />
                </div> */}
            </Tabs>
        </div>
    );
};
